import SRCards from '../../components/SRCards';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    MenuItem,
    Select,
    Snackbar,
    Container,
    FormControl,
    InputLabel,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import SelectCompany from '../../components/SelectCompany';
import FileUploadSection from '../../components/FileUpload';
import UploadApi from '../../services/upload.service';
import { CHECK_VALUE } from '../../common/data-validator';
import CommonServiceApi from '../../services/common-service';

type SnackbarSeverity = 'success' | 'error' | 'info' | 'warning';
type SnackbarState = {
    open: boolean;
    message: string;
    severity: SnackbarSeverity;
};

const UPLOAD_CONFIG = {
    custom_invoice: {
        name: 'Invoice handlebars',
        s3Path: `billing-templates`,
        filesRequired: 3,
        files: [
            {
                label: 'Invoice',
                allowedTypes: ['.handlebars'],
                fileName: 'invoice.handlebars',
            },
            {
                label: 'Estimate',
                allowedTypes: ['.handlebars'],
                fileName: 'estimate.handlebars',
            },
            {
                label: 'Independent Estimate',
                allowedTypes: ['.handlebars'],
                fileName: 'independent-estimate.handlebars',
            },
        ],
    },
    work_order: {
        name: 'Work order handlebar',
        s3Path: `billing-templates`,
        filesRequired: 1,
        files: [
            {
                label: 'Work Order',
                allowedTypes: ['.handlebars'],
                fileName: 'ticket.handlebars',
            },
        ],
    },
};

const FileUpload = () => {
    const [selectedUploadConfig, setSelectedUploadConfig] = useState('');
    const [files, setFiles] = useState<File[]>([]);
    const [selectedCompany, setSelectedCompany] = useState<any>(null);
    const [isCustomPdfEnabled, setIsCustomPdfEnabled] = useState(false);
    const [downloadLinks, setDownloadLinks] = useState<Record<string, string>>(
        {}
    );
    const [isCheckingFiles, setIsCheckingFiles] = useState(false);
    const [snackbar, setSnackbar] = useState<SnackbarState>({
        open: false,
        message: '',
        severity: 'info',
    });
    const commonService = useMemo(() => new CommonServiceApi(), []);

    const handleFileTypeChange = (e: React.ChangeEvent<{ value: unknown }>) => {
        const value = e.target.value as string;
        setSelectedUploadConfig(value);
        setFiles([]);
    };

    const handleFileChange = (index: number, file: File | null) => {
        const updated = [...files];
        updated[index] = file!;
        setFiles(updated);
    };

    const handleUpload = async (index: number) => {
        if (!selectedUploadConfig) {
            setSnackbar({
                open: true,
                message: 'Please select a file type first.',
                severity: 'warning',
            });
            return;
        }
        const companyId = localStorage.getItem('selectedCompanyId');
        if (!CHECK_VALUE(companyId)) {
            setSnackbar({
                open: true,
                message: 'Please select a company before uploading.',
                severity: 'warning',
            });
            return;
        }
        const companyIdValue = String(companyId);
        const file = files[index];
        if (!file) {
            setSnackbar({
                open: true,
                message: `Please choose a file before uploading.`,
                severity: 'error',
            });
            return;
        }
        const config =
            UPLOAD_CONFIG[selectedUploadConfig as keyof typeof UPLOAD_CONFIG];
        const s3Path = getS3Path();
        const expectedFileName = config.files[index]?.fileName;
        if (CHECK_VALUE(expectedFileName) && expectedFileName !== file.name) {
            setSnackbar({
                open: true,
                message: `Invalid file name "${file.name}". Expected: "${expectedFileName}"`,
                severity: 'error',
            });
            return;
        }
        UploadApi.uploadFileToS3(s3Path, file, companyIdValue)
            .then(() => {
                setSnackbar({
                    open: true,
                    message: `Uploaded "${file.name}" to ${s3Path}`,
                    severity: 'success',
                });
                if (
                    selectedUploadConfig === 'custom_invoice' ||
                    selectedUploadConfig === 'work_order'
                ) {
                    refreshDownloadLinks(companyIdValue, selectedUploadConfig);
                }
            })
            .catch(() => {
                setSnackbar({
                    open: true,
                    message: `Failed to upload "${file.name}"`,
                    severity: 'error',
                });
            });
    };

    const getSelectedConfig = () => {
        return {
            ...UPLOAD_CONFIG[
                selectedUploadConfig as keyof typeof UPLOAD_CONFIG
            ],
            s3Path: getS3Path(),
        };
    };

    const getS3Path = () => {
        const companyId = localStorage.getItem('selectedCompanyId');
        return `${
            UPLOAD_CONFIG[selectedUploadConfig as keyof typeof UPLOAD_CONFIG]
                .s3Path
        }/${companyId}`;
    };

    const refreshDownloadLinks = useCallback(
        async (companyId: string, uploadType: string) => {
            const config =
                UPLOAD_CONFIG[uploadType as keyof typeof UPLOAD_CONFIG];
            if (!config) {
                setDownloadLinks({});
                return;
            }
            setIsCheckingFiles(true);
            try {
                const results = await Promise.all(
                    (config.files || []).map(async (fileDef) => {
                        const fileName = fileDef.fileName || '';
                        if (!CHECK_VALUE(fileName)) {
                            return { fileName, exists: false, downloadUrl: '' };
                        }
                        try {
                            const response =
                                uploadType === 'work_order'
                                    ? await UploadApi.getWorkOrderTemplateFileInfo(
                                          companyId,
                                          fileName
                                      )
                                    : await UploadApi.getInvoicePdfTemplateFileInfo(
                                          companyId,
                                          fileName
                                      );
                            return {
                                fileName,
                                exists: response?.exists,
                                downloadUrl: response?.downloadUrl,
                            };
                        } catch (err) {
                            return { fileName, exists: false, downloadUrl: '' };
                        }
                    })
                );
                const links = results.reduce(
                    (acc: Record<string, string>, item) => {
                        if (item.exists && item.downloadUrl) {
                            acc[item.fileName] = item.downloadUrl;
                        }
                        return acc;
                    },
                    {}
                );
                setDownloadLinks(links);
            } catch (err) {
                setDownloadLinks({});
            } finally {
                setIsCheckingFiles(false);
            }
        },
        []
    );

    useEffect(() => {
        const fetchEnabledFeatures = async () => {
            if (!selectedCompany || !selectedCompany.value) {
                setIsCustomPdfEnabled(false);
                setDownloadLinks({});
                return;
            }
            try {
                const result: any = await commonService.getEnabledFeatures({
                    company: selectedCompany.value,
                });
                const data =
                    typeof result === 'string' ? JSON.parse(result) : result;
                const enabledFeatures = data?.enabledFeatures || [];
                const enabled = enabledFeatures.includes('CUSTOM_INVOICE_PDF');
                setIsCustomPdfEnabled(enabled);
            } catch (err) {
                setIsCustomPdfEnabled(false);
                setDownloadLinks({});
            }
        };
        fetchEnabledFeatures();
    }, [selectedCompany, commonService]);

    useEffect(() => {
        if (!selectedCompany || !selectedCompany.value) {
            setDownloadLinks({});
            return;
        }
        const companyId = String(selectedCompany.value);
        if (selectedUploadConfig === 'custom_invoice') {
            if (isCustomPdfEnabled) {
                refreshDownloadLinks(companyId, selectedUploadConfig);
            } else {
                setDownloadLinks({});
            }
            return;
        }
        if (selectedUploadConfig === 'work_order') {
            refreshDownloadLinks(companyId, selectedUploadConfig);
            return;
        }
        setDownloadLinks({});
    }, [
        selectedCompany,
        selectedUploadConfig,
        isCustomPdfEnabled,
        refreshDownloadLinks,
    ]);

    return (
        <>
            <SRCards />
            <Container>
                <SelectCompany optionHandler={setSelectedCompany} />

                {!selectedCompany || !selectedCompany.value ? (
                    <Alert severity='info' style={{ marginTop: 20 }}>
                        Please select a company to upload or download templates.
                    </Alert>
                ) : (
                    <>
                        <FormControl fullWidth style={{ marginTop: 20 }}>
                            <InputLabel id='collection-label'>
                                File Type
                            </InputLabel>
                            <Select
                                labelId='collection-label'
                                value={selectedUploadConfig}
                                onChange={handleFileTypeChange}
                            >
                                <MenuItem value='custom_invoice'>
                                    {UPLOAD_CONFIG.custom_invoice.name}
                                </MenuItem>
                                <MenuItem value='work_order'>
                                    {UPLOAD_CONFIG.work_order.name}
                                </MenuItem>
                            </Select>
                        </FormControl>

                        {selectedUploadConfig === 'custom_invoice' &&
                            !isCustomPdfEnabled && (
                                <Alert severity='info' style={{ marginTop: 20 }}>
                                    Custom Invoice PDF is not enabled for this
                                    company. Enable it to upload or download
                                    templates.
                                </Alert>
                            )}

                        {selectedUploadConfig &&
                            !(
                                selectedUploadConfig === 'custom_invoice' &&
                                !isCustomPdfEnabled
                            ) && (
                                <FileUploadSection
                                    key={selectedUploadConfig}
                                    config={getSelectedConfig()}
                                    onFileChange={handleFileChange}
                                    onUpload={handleUpload}
                                    onDownload={(index: number) => {
                                        const config =
                                            UPLOAD_CONFIG[
                                                selectedUploadConfig as keyof typeof UPLOAD_CONFIG
                                            ];
                                        const fileName =
                                            config.files?.[index]?.fileName ||
                                            '';
                                        const url = downloadLinks[fileName];
                                        if (url) {
                                            window.open(url, '_blank');
                                        }
                                    }}
                                    downloadLinks={downloadLinks}
                                    showDownload={
                                        selectedUploadConfig ===
                                            'custom_invoice' ||
                                        selectedUploadConfig === 'work_order'
                                    }
                                    isCheckingFiles={isCheckingFiles}
                                />
                            )}
                    </>
                )}

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={3000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                >
                    <Alert
                        onClose={() =>
                            setSnackbar({ ...snackbar, open: false })
                        }
                        severity={snackbar.severity}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Container>
        </>
    );
};

export default FileUpload;
